import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

/**
 * IMPORTANT : mode HASH obligatoire.
 * Dans une webview Capacitor, l'app est servie depuis capacitor:// (iOS) ou
 * http://localhost (Android) sans serveur capable de faire un fallback
 * SPA. Le mode history provoque des 404 / écrans blancs au reload ou au
 * retour depuis un deep link. createWebHashHistory() évite tout ça.
 */

declare module 'vue-router' {
  interface RouteMeta {
    /** titre de l'écran */
    title?: string
    /**
     * L'onglet de la tab bar à garder allumé sur une **sous-page** (classement,
     * détail de match) : sans lui, la bulle disparaîtrait dès qu'on
     * quitte une route d'onglet. Vaut un `name` de `src/config/tabs.ts`.
     */
    tab?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/CompetitionsView.vue'),
    meta: { title: 'Accueil' },
  },
  {
    path: '/competition',
    name: 'competition',
    component: () => import('@/views/CompetitionView.vue'),
    meta: { title: 'Compétition' },
  },
  /*
   * Sous-pages de l'onglet « Compétition » : elles gardent sa bulle allumée via
   * `meta.tab` (cf. `TabBar.vue`). `props: true` passe les paramètres de route
   * au composant — ils arrivent toujours en **chaînes**, même `:challengeId` :
   * c'est à la vue de les convertir (`Number(...)`).
   */
  {
    path: '/competition/ranking/:challengeId/:stepId?',
    name: 'ranking',
    component: () => import('@/views/RankingView.vue'),
    props: true,
    meta: { title: 'Classement', tab: 'competition' },
  },
  {
    // l'id d'un match est un uuid composite, pas un entier
    path: '/match/:id',
    name: 'match',
    component: () => import('@/views/MatchDetailView.vue'),
    props: true,
    meta: { title: 'Match', tab: 'competition' },
  },
  {
    path: '/teams',
    name: 'teams',
    component: () => import('@/views/TeamsView.vue'),
    meta: { title: 'Équipes' },
  },
  {
    // sous-page de l'onglet « Équipes » ; l'id est un uuid composite, pas un entier
    path: '/team/:id',
    name: 'team',
    component: () => import('@/views/TeamView.vue'),
    props: true,
    meta: { title: 'Équipe', tab: 'teams' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { title: 'Profil' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/home',
  },
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  // Le scroll est géré par notre conteneur .app-main, pas par window :
  // on le remet à zéro manuellement dans App.vue au changement de route.
  scrollBehavior: () => ({ top: 0 }),
})

export default router
